import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const RegionalSettingsModal = ({ open, onClose, onSave }) => {
  // Load saved settings from localStorage or use defaults
  const loadSavedSettings = () => {
    try {
      const savedSettings = JSON.parse(localStorage.getItem('regionalSettings') || '{}');
      return {
        language: savedSettings.language || 'en-US',
        country: savedSettings.country || 'IN',
        currency: savedSettings.currency || 'INR'
      };
    } catch (error) {
      console.error('Error loading saved settings:', error);
      return {
        language: 'en-US',
        country: 'IN',
        currency: 'INR'
      };
    }
  };

  // Initialize with default values, will be updated when modal opens
  const [language, setLanguage] = useState('en-US');
  const [country, setCountry] = useState('IN');
  const [currency, setCurrency] = useState('INR');

  // Load saved settings when modal opens - only load once when opening
  useEffect(() => {
    if (open) {
      const settings = loadSavedSettings();
      setLanguage(settings.language);
      setCountry(settings.country);
      setCurrency(settings.currency);
    }
  }, [open]);

  const languages = [
    { value: 'en-US', label: 'English (United States)' },
    { value: 'en-GB', label: 'English (United Kingdom)' },
    { value: 'hi', label: 'Hindi' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'ja', label: 'Japanese' },
    { value: 'zh', label: 'Chinese' }
  ];

  const countries = [
    { value: 'IN', label: 'India' },
    { value: 'US', label: 'United States' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'JP', label: 'Japan' },
    { value: 'CN', label: 'China' },
    { value: 'SG', label: 'Singapore' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'MY', label: 'Malaysia' }
  ];

  const currencies = [
    { value: 'INR', label: 'INR-₹' },
    { value: 'USD', label: 'USD-$' },
    { value: 'GBP', label: 'GBP-£' },
    { value: 'EUR', label: 'EUR-€' },
    { value: 'AUD', label: 'AUD-A$' },
    { value: 'CAD', label: 'CAD-C$' },
    { value: 'JPY', label: 'JPY-¥' },
    { value: 'CNY', label: 'CNY-¥' },
    { value: 'SGD', label: 'SGD-S$' },
    { value: 'AED', label: 'AED-د.إ' },
    { value: 'MYR', label: 'MYR-RM' }
  ];

  const handleSave = () => {
    // Save settings to localStorage
    try {
      const settingsToSave = {
        language,
        country,
        currency
      };
      localStorage.setItem('regionalSettings', JSON.stringify(settingsToSave));
      console.log('✅ Regional settings saved to localStorage:', settingsToSave);
    } catch (error) {
      console.error('❌ Error saving regional settings to localStorage:', error);
    }

    // Call the onSave callback with the new settings
    if (onSave) {
      onSave({ language, country, currency });
    }
    onClose();
  };

  const handleCancel = () => {
    // Reset to saved values from localStorage
    const settings = loadSavedSettings();
    setLanguage(settings.language);
    setCountry(settings.country);
    setCurrency(settings.currency);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        timeout: 0
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'none !important'
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            transition: 'opacity 0ms !important'
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1.5,
          pt: 2.5,
          px: 3
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#20202A'
          }}
        >
          Regional Setting
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          px: 3, 
          py: 2, 
          pt: 2,
          overflow: 'hidden',
          '&.MuiDialogContent-root': {
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Languages */}
          <FormControl fullWidth sx={{ mt: 0 }}>
            <InputLabel
              id="language-label"
              shrink={true}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#20202A',
                position: 'relative',
                transform: 'none',
                marginBottom: '6px',
                '&.Mui-focused': {
                  color: '#20202A'
                }
              }}
            >
              Languages
            </InputLabel>
            <Select
              labelId="language-label"
              value={language}
              label=""
              displayEmpty
              onChange={(e) => setLanguage(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(232, 233, 237, 1)',
                    '& .MuiMenuItem-root': {
                      backgroundColor: 'rgba(232, 233, 237, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(220, 221, 225, 1)'
                      }
                    }
                  }
                }
              }}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none',
                  borderWidth: '1px',
                  borderRadius: '8px'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#20202A'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none',
                  borderWidth: '2px'
                }
              }}
            >
              {languages.map((lang) => (
                <MenuItem
                  key={lang.value}
                  value={lang.value}
                  sx={{
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '14px'
                  }}
                >
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Country/Region */}
          <FormControl fullWidth>
            <InputLabel
              id="country-label"
              shrink={true}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#20202A',
                position: 'relative',
                transform: 'none',
                marginBottom: '6px',
                '&.Mui-focused': {
                  color: '#20202A'
                }
              }}
            >
              Country/Region
            </InputLabel>
            <Select
              labelId="country-label"
              value={country}
              label=""
              displayEmpty
              onChange={(e) => setCountry(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(232, 233, 237, 1)',
                    '& .MuiMenuItem-root': {
                      backgroundColor: 'rgba(232, 233, 237, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(220, 221, 225, 1)'
                      }
                    }
                  }
                }
              }}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none',
                  borderWidth: '1px',
                  borderRadius: '8px'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none',
                  borderWidth: '2px'
                }
              }}
            >
              {countries.map((countryOption) => (
                <MenuItem
                  key={countryOption.value}
                  value={countryOption.value}
                  sx={{
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '14px'
                  }}
                >
                  {countryOption.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Currency */}
          <FormControl fullWidth>
            <InputLabel
              id="currency-label"
              shrink={true}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#20202A',
                position: 'relative',
                transform: 'none',
                marginBottom: '6px',
                '&.Mui-focused': {
                  color: '#20202A'
                }
              }}
            >
              Currency
            </InputLabel>
            <Select
              labelId="currency-label"
              value={currency}
              label=""
              displayEmpty
              onChange={(e) => setCurrency(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(232, 233, 237, 1)',
                    '& .MuiMenuItem-root': {
                      backgroundColor: 'rgba(232, 233, 237, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(220, 221, 225, 1)'
                      }
                    }
                  }
                }
              }}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                backgroundColor: 'rgba(232, 233, 237, 1)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none',
                  borderWidth: '1px',
                  borderRadius: '8px'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'none',
                  borderWidth: '2px'
                }
              }}
            >
              {currencies.map((curr) => (
                <MenuItem
                  key={curr.value}
                  value={curr.value}
                  sx={{
                    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                    fontSize: '14px'
                  }}
                >
                  {curr.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          flexDirection: 'column',
          gap: 1,
          // borderTop: '1px solid #E8E9ED'
        }}
      >
        <Button
          onClick={handleSave}
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: '#20202A',
            color: '#FBFBFB',
            borderRadius: '8px',
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            '&:hover': {
              backgroundColor: '#333'
            }
          }}
        >
          Save
        </Button>
        <Button
          onClick={handleCancel}
          variant="outlined"
          fullWidth
          sx={{
            borderColor: '#E8E9ED',
            color: '#20202A',
            borderRadius: '8px',
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            '&:hover': {
              borderColor: 'none',
              backgroundColor: 'transparent'
            }
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegionalSettingsModal;

